import { get } from "lodash"
import { OK } from "helpers/data-statuses"
import { getIsOpPro } from "modules/account-root/selectors"
import { useSelector } from "react-redux"
import { ReduxState } from "model/state"
import { Uuid } from "helpers/types/uuid"

const EMPTY_FEATURES: Record<string, boolean> = {}

export function useAccountAlias(): string {
  return useSelector((state: ReduxState) => state.global.selected.account)
}

export function useAccountId(): Uuid {
  return useSelector((state: ReduxState) => state.global.selected.accountId)
}

export function useUserId(): Uuid {
  return useSelector((state: ReduxState) => state.user.id)
}

export function useUserName(): string | undefined {
  return useSelector((state: ReduxState) => state.user.name)
}

export function useUserEmail(): string | undefined {
  return useSelector((state: ReduxState) => state.user.email)
}

export function useDefaultCurrencyId(): string {
  return useSelector((state: ReduxState) => get(state, "global.selected.accountMeta.defaultCurrency"))
}

export function useIsOpPro(): boolean | undefined {
  return useSelector(getIsOpPro)
}

export function useIsIntradayEnabled(): boolean {
  return useSelector((state: ReduxState) => get(state, "global.selected.accountMeta.intradayEnabled", false))
}

export function useEnabledFeatures(): Record<string, boolean> {
  // ?? not get's default arg: get only substitutes for undefined, so the loading-state
  // `data: null` would slip through and crash flag reads.
  return useSelector((state: ReduxState) => get(state, "global.data.enabledFeatures.data") ?? EMPTY_FEATURES)
}

export function useIsEndVsStartDateComparisonEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.end_vs_start ?? false
}

export function useAreThemesEnabled(): boolean | null {
  return useSelector((state: ReduxState) => {
    const result = state.global.data.enabledFeatures
    if (!result || result.status !== OK) {
      return null
    }
    return result.data?.theming ?? false
  })
}

export function useIsAiTermsAccepted(): boolean {
  return useSelector((state: ReduxState) => get(state, "global.data.aiTermsAndConditions.data.enabled", false))
}

export function useIsDashboardOptimizeExperimentsFeatureEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.optimize_experiments ?? false
}

export function useIsDashboardSelectionSizingFeatureEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.selection_sizing ?? false
}

export function useIsDashboardEqualIdioWeightingFeatureEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.equal_idio ?? false
}

export function useIsDashboardOpLayoutEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.dashboard_op_layout ?? false
}

export function useIsAiChatFeatureEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures?.ai_chat ?? false
}

export function useIsBattingSluggingEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.batting_slugging ?? false
}

export function useIsMixpanelSessionReplayEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.mixpanel_replay ?? false
}

export function useIsRfrAttributionEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.rfr_attribution ?? false
}

export function useIsAiEverywhereEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.ai_everywhere ?? false
}

export function useIsManagedAgentEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.managed_agent ?? false
}

export function useIsAiModulePinningEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.ai_module_pinning ?? false
}

export function useIsMultiDashboardsEnabled(): boolean {
  const enabledFeatures = useEnabledFeatures()
  return enabledFeatures.multi_dashboards ?? false
}

export const AI_MODULE_LIMIT = 8
